import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'
import { Subscription } from 'rxjs/Subscription'
import * as constants from './constants'
import * as actions from './actions'
import { getParticipants } from './selectors'
import { makeActiveConversations } from 'app/modules/conversation/selectors'
import * as SocketConstants from 'app/modules/socket/constants'
import { currentUser } from 'app/modules/user/selectors'
import { UserTypes } from 'utils/UserTypes'

const TYPING_TIMEOUT = 5000
const TYPING_STARTED_EVENT = 'typing:started'
const TYPING_FINISHED_EVENT = 'typing:finished'

const joinUserChannelEpic = (action$, { getState }) => {
  const activeConversationsSelector = makeActiveConversations()

  return action$
    .ofType(SocketConstants.JOIN_CHANNEL_PENDING)
    .pluck('payload')
    .filter((payload) => payload.clusterName === SocketConstants.SocketCluster.MESSAGES)
    .filter((payload) => payload.channelName === SocketConstants.SocketChannel.ORG_PRIVATE)
    .switchMap(({ channel, leaveChannel$ }) => {
      return Observable.create((observer) => {
        // handle incoming typing indicators
        const handleTypingEvent = (payload, actionCreator) => {
          const { userId, userType, conversationId } = payload

          // don't re-dispatch own typing indicators
          const user = currentUser(getState())
          if (userType === UserTypes.USER && userId === user.id) {
            return
          }

          const existingConversationList = activeConversationsSelector(getState())

          // dont dispatch typing indicators for conversations not in the current list
          if (!existingConversationList.has(conversationId)) {
            return
          }

          observer.next(actionCreator(payload))
        }

        channel.on(TYPING_STARTED_EVENT, (payload) => {
          handleTypingEvent(payload, actions.typing)
        })

        channel.on(TYPING_FINISHED_EVENT, (payload) => {
          handleTypingEvent(payload, actions.typingFinished)
        })
      })
        .takeUntil(leaveChannel$)
    })
}

const joinPrivateOrgChannelEpic = (action$, { getState }) =>
  action$
    .ofType(SocketConstants.JOIN_CHANNEL_SUCCESS)
    .pluck('payload')
    .filter((payload) => payload.clusterName === SocketConstants.SocketCluster.MESSAGES)
    .filter((payload) => payload.channelName === SocketConstants.SocketChannel.USER)
    .switchMap(({ channel, leaveChannel$ }) => {
      const sendTypingIndicators$ = action$
        .ofType(constants.USER_TYPING, constants.USER_TYPING_FINISHED)
        .map(({ type, payload }) => {
          const user = currentUser(getState())
          return {
            event: type === constants.USER_TYPING ? TYPING_STARTED_EVENT : TYPING_FINISHED_EVENT,
            data: {
              userId: user.id,
              userType: UserTypes.USER,
              participants: getParticipants(getState()),
              ...payload,
            },
          }
        })

      return Observable.create(() => {
        const subscription = new Subscription()

        // dispatch outgoing typing indicators to user channel
        subscription.add(sendTypingIndicators$.subscribe({
          next: ({ event, data }) => {
            channel.push(event, data)
          },
        }))

        return () => {
          subscription.unsubscribe()
        }
      })
        .takeUntil(leaveChannel$)
    })

const typingEpic = (action$) =>
  action$
    .ofType(constants.TYPING)
    .pluck('payload')
    .mergeMap(({ userId, userType, conversationId }) => {
      return Observable.merge(
        action$
          .ofType(constants.TYPING, constants.TYPING_FINISHED)
          .filter((action) => {
            return action.payload.userId === userId &&
              action.payload.userType === userType &&
              action.payload.conversationId === conversationId
          }),
        Observable.timer(TYPING_TIMEOUT),
      )
        .first()
        .filter((action) => !action)
        .map(() => actions.typingFinished({
          userId,
          userType,
          conversationId,
        }))
    })

export default combineEpics(
  joinUserChannelEpic,
  joinPrivateOrgChannelEpic,
  typingEpic,
)



// WEBPACK FOOTER //
// ./src/scripts/app/modules/typing/epic.js